<template>
  <div class="space-y-8">
    <!-- Late Tasks Chart -->
    <div class="bg-white rounded-2xl border border-gray-200 shadow-md p-6">
      <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <svg class="w-5 h-5 text-amber-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Late Tasks by Board
      </h2>
      <p class="text-sm text-gray-500 mb-4">
        Tasks past their due date that are not yet completed.
      </p>
      <div v-if="lateTasksLoading" class="flex items-center justify-center py-16">
        <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
      </div>
      <div v-else-if="lateTasksData && lateTasksData.by_board && lateTasksData.by_board.length > 0">
        <div class="mb-4">
          <span class="text-2xl font-bold text-amber-600">{{ lateTasksData.total }}</span>
          <span class="text-gray-600 ml-2">total late tasks</span>
        </div>
        <apexchart type="bar" height="300" :options="lateTasksChartOptions" :series="lateTasksChartSeries"
          @dataPointSelection="handleBarClick" />
      </div>
      <div v-else class="text-center py-16 text-gray-500">
        <svg class="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="text-gray-600">No late tasks</p>
        <p class="text-sm mt-1">All tasks are on track or completed.</p>
      </div>
    </div>

    <!-- Late Tasks by Employee Chart -->
    <div class="bg-white rounded-2xl border border-gray-200 shadow-md p-6">
      <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <svg class="w-5 h-5 text-amber-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        Late Tasks by Employee
      </h2>
      <p class="text-sm text-gray-500 mb-4">
        Number of late tasks assigned to each employee.
      </p>
      <div v-if="lateTasksLoading" class="flex items-center justify-center py-16">
        <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
      </div>
      <div v-else-if="lateTasksData && lateTasksData.by_assignee && lateTasksData.by_assignee.length > 0">
        <div class="mb-4">
          <span class="text-2xl font-bold text-amber-600">{{ lateTasksData.by_assignee.length }}</span>
          <span class="text-gray-600 ml-2">employees with late tasks</span>
        </div>
        <apexchart type="bar" height="300" :options="lateTasksByEmployeeChartOptions"
          :series="lateTasksByEmployeeChartSeries" @dataPointSelection="handleEmployeeBarClick" />
      </div>
      <div v-else class="text-center py-16 text-gray-500">
        <svg class="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="text-gray-600">No late tasks by employee</p>
        <p class="text-sm mt-1">No employees have late tasks assigned.</p>
      </div>
    </div>

    <!-- Late Tasks Modal (board bar clicked) -->
    <base-modal v-if="selectedBoardForLateTasks" :title="getLateTasksModalTitle" height="70vh" width="90vw"
      @close="closeLateTasksModal">
      <div class="px-8 py-4">
        <div v-if="lateTasksForBoardLoading" class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
        </div>
        <div v-else-if="lateTasksForBoard && lateTasksForBoard.length > 0" class="space-y-3">
          <div v-for="task in lateTasksForBoard" :key="task.id" @click="openTask(task.id)"
            class="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer p-4 border-l-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            :style="{ borderLeftColor: getPriorityColor(task.priority) }">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-3 flex-wrap">
                <h3 class="text-base font-semibold text-gray-900 truncate">{{ task.title }}</h3>
                <span class="text-xs px-3 py-1 rounded-full text-white shrink-0"
                  :style="{ backgroundColor: getPriorityColor(task.priority) }">
                  {{ task.priority }}
                </span>
                <span class="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 shrink-0">
                  {{ getStatusLabel(task.status) }}
                </span>
              </div>
              <div v-if="task.assignees && task.assignees.length > 0" class="flex items-center gap-2 mt-2">
                <span class="text-xs text-gray-500">Assigned to:</span>
                <span class="text-sm text-gray-700">{{ (task.assignees || []).map(function (a) {
                  return a.name;
                }).join(', ') }}</span>
              </div>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <span v-if="task.due_date" class="text-sm text-amber-600 font-medium">{{ formatDate(task.due_date)
              }}</span>
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-12 text-gray-500">
          <p>No late tasks available for this board.</p>
        </div>
      </div>
    </base-modal>

    <!-- Late Tasks by Employee Modal (employee bar clicked) -->
    <base-modal v-if="selectedAssigneeForLateTasks" :title="getEmployeeLateTasksModalTitle" height="70vh" width="90vw"
      @close="closeEmployeeLateTasksModal">
      <div class="px-8 py-4">
        <div v-if="lateTasksForAssigneeLoading" class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
        </div>
        <div v-else-if="lateTasksForAssignee && lateTasksForAssignee.length > 0" class="space-y-3">
          <div v-for="task in lateTasksForAssignee" :key="task.id" @click="openTask(task.id)"
            class="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer p-4 border-l-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            :style="{ borderLeftColor: getPriorityColor(task.priority) }">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-3 flex-wrap">
                <h3 class="text-base font-semibold text-gray-900 truncate">{{ task.title }}</h3>
                <span class="text-xs px-3 py-1 rounded-full text-white shrink-0"
                  :style="{ backgroundColor: getPriorityColor(task.priority) }">
                  {{ task.priority }}
                </span>
                <span class="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 shrink-0">
                  {{ getStatusLabel(task.status) }}
                </span>
              </div>
              <p v-if="task.board && task.board.name" class="text-sm text-gray-500 mt-2">
                {{ (task.board.master_board && task.board.master_board.name) ? (task.board.master_board.name + ' / ' +
                  task.board.name) : task.board.name }}
              </p>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <span v-if="task.due_date" class="text-sm text-amber-600 font-medium">{{ formatDate(task.due_date)
              }}</span>
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-12 text-gray-500">
          <p>No late tasks available for this employee.</p>
        </div>
      </div>
    </base-modal>

    <!-- Placeholder for future charts -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div class="bg-white rounded-2xl border border-gray-200 shadow-md p-6 border-dashed">
        <h3 class="text-base font-medium text-gray-500 mb-2">More charts coming soon</h3>
        <p class="text-sm text-gray-400">Tasks by priority, status distribution, and more.</p>
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag';

export default {
  name: 'tasks-charts',
  data() {
    return {
      lateTasksData: null,
      lateTasksLoading: false,
      selectedBoardForLateTasks: null,
      lateTasksForBoard: [],
      lateTasksForBoardLoading: false,
      selectedAssigneeForLateTasks: null,
      lateTasksForAssignee: [],
      lateTasksForAssigneeLoading: false,
    };
  },
  computed: {
    getLateTasksModalTitle() {
      const b = this.selectedBoardForLateTasks;
      if (!b) return 'Late Tasks';
      const label = b.master_board_name ? (b.master_board_name + ' / ' + b.board_name) : b.board_name;
      return 'Late Tasks - ' + label;
    },
    getEmployeeLateTasksModalTitle() {
      const a = this.selectedAssigneeForLateTasks;
      if (!a) return 'Late Tasks';
      return 'Late Tasks - ' + (a.assignee_name || 'Employee');
    },
    lateTasksChartOptions() {
      const categories = (this.lateTasksData && this.lateTasksData.by_board)
        ? this.lateTasksData.by_board.map(function (b) {
          const label = b.master_board_name ? (b.master_board_name + ' / ' + b.board_name) : b.board_name;
          return label.length > 25 ? label.substring(0, 22) + '...' : label;
        })
        : [];
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
        },
        xaxis: {
          categories,
          labels: {
            rotate: -45,
            rotateAlways: categories.length > 4,
          },
          title: {
            text: 'Board',
            style: { fontSize: '12px', fontWeight: 'bold' },
          },
        },
        yaxis: {
          title: {
            text: 'Number of Late Tasks',
            style: { fontSize: '12px', fontWeight: 'bold' },
          },
        },
        colors: ['#F59E0B'],
        plotOptions: {
          bar: {
            columnWidth: '60%',
            borderRadius: 4,
          },
        },
        dataLabels: {
          enabled: true,
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + ' late task' + (val !== 1 ? 's' : '');
            },
          },
        },
      };
    },
    lateTasksChartSeries() {
      const data = (this.lateTasksData && this.lateTasksData.by_board)
        ? this.lateTasksData.by_board.map(function (b) { return b.count; })
        : [];
      return [{ name: 'Late Tasks', data }];
    },
    lateTasksByEmployeeChartOptions() {
      const categories = (this.lateTasksData && this.lateTasksData.by_assignee)
        ? this.lateTasksData.by_assignee.map(function (a) {
          const name = a.assignee_name || 'Unknown';
          return name.length > 20 ? name.substring(0, 17) + '...' : name;
        })
        : [];
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
        },
        xaxis: {
          categories,
          labels: {
            rotate: -45,
            rotateAlways: categories.length > 4,
          },
          title: {
            text: 'Employee',
            style: { fontSize: '12px', fontWeight: 'bold' },
          },
        },
        yaxis: {
          title: {
            text: 'Number of Late Tasks',
            style: { fontSize: '12px', fontWeight: 'bold' },
          },
        },
        colors: ['#F59E0B'],
        plotOptions: {
          bar: {
            columnWidth: '60%',
            borderRadius: 4,
          },
        },
        dataLabels: {
          enabled: true,
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + ' late task' + (val !== 1 ? 's' : '');
            },
          },
        },
      };
    },
    lateTasksByEmployeeChartSeries() {
      const data = (this.lateTasksData && this.lateTasksData.by_assignee)
        ? this.lateTasksData.by_assignee.map(function (a) { return a.count; })
        : [];
      return [{ name: 'Late Tasks', data }];
    },
  },
  mounted() {
    this.fetchLateTasksStats();
  },
  methods: {
    async fetchLateTasksStats() {
      this.lateTasksLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetLateTasksStats {
              lateTasksStats {
                error
                error_code
                message
                data {
                  by_board {
                    board_id
                    board_name
                    master_board_name
                    count
                  }
                  by_assignee {
                    assignee_id
                    assignee_name
                    count
                  }
                  total
                }
              }
            }
          `,
          fetchPolicy: 'network-only',
        });
        const payload = data && data.lateTasksStats;
        if (payload && !payload.error && payload.data) {
          this.lateTasksData = payload.data;
        } else {
          this.lateTasksData = { by_board: [], by_assignee: [], total: 0 };
        }
      } catch (e) {
        this.lateTasksData = { by_board: [], by_assignee: [], total: 0 };
      } finally {
        this.lateTasksLoading = false;
      }
    },
    handleBarClick(event, chartContext, config) {
      const index = config.dataPointIndex;
      const boards = this.lateTasksData && this.lateTasksData.by_board;
      if (boards && boards[index]) {
        this.selectedBoardForLateTasks = boards[index];
        this.fetchLateTasksByBoard();
      }
    },
    handleEmployeeBarClick(event, chartContext, config) {
      const index = config.dataPointIndex;
      const assignees = this.lateTasksData && this.lateTasksData.by_assignee;
      if (assignees && assignees[index]) {
        this.selectedAssigneeForLateTasks = assignees[index];
        this.fetchLateTasksByAssignee();
      }
    },
    async fetchLateTasksByBoard() {
      const b = this.selectedBoardForLateTasks;
      if (!b || !b.board_id) return;
      this.lateTasksForBoardLoading = true;
      this.lateTasksForBoard = [];
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetLateTasksByBoard($board_id: ID!) {
              lateTasksByBoard(board_id: $board_id) {
                id
                title
                priority
                status
                due_date
                assignees {
                  id
                  name
                }
              }
            }
          `,
          variables: { board_id: b.board_id },
          fetchPolicy: 'network-only',
        });
        this.lateTasksForBoard = (data && data.lateTasksByBoard) ? data.lateTasksByBoard : [];
      } catch (e) {
        this.lateTasksForBoard = [];
      } finally {
        this.lateTasksForBoardLoading = false;
      }
    },
    closeLateTasksModal() {
      this.selectedBoardForLateTasks = null;
      this.lateTasksForBoard = [];
    },
    async fetchLateTasksByAssignee() {
      const a = this.selectedAssigneeForLateTasks;
      if (!a || !a.assignee_id) return;
      this.lateTasksForAssigneeLoading = true;
      this.lateTasksForAssignee = [];
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetLateTasksByAssignee($assignee_id: ID!) {
              lateTasksByAssignee(assignee_id: $assignee_id) {
                id
                title
                priority
                status
                due_date
                board {
                  id
                  name
                  master_board {
                    id
                    name
                  }
                }
              }
            }
          `,
          variables: { assignee_id: a.assignee_id },
          fetchPolicy: 'network-only',
        });
        this.lateTasksForAssignee = (data && data.lateTasksByAssignee) ? data.lateTasksByAssignee : [];
      } catch (e) {
        this.lateTasksForAssignee = [];
      } finally {
        this.lateTasksForAssigneeLoading = false;
      }
    },
    closeEmployeeLateTasksModal() {
      this.selectedAssigneeForLateTasks = null;
      this.lateTasksForAssignee = [];
    },
    getPriorityColor(priority) {
      const colors = {
        low: '#10B981',
        medium: '#F59E0B',
        high: '#EF4444',
        critical: '#7C3AED',
      };
      return colors[(priority || 'medium').toLowerCase()] || colors.medium;
    },
    getStatusLabel(status) {
      const labels = {
        todo: 'To Do',
        in_progress: 'In Progress',
        paused: 'Paused',
        in_review: 'In Review',
        done: 'Done',
      };
      return labels[(status || 'todo').toLowerCase()] || status;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    openTask(taskId) {
      this.$router.push('/tasks/task/' + taskId);
    },
  },
};
</script>
